
import type { FileDownloadMetrics } from "@/stores/bucket_events";
import type { DownloadEvent } from "@/stores/bucket_events";
import type { StorageLocation, FlyRegionLocation } from "@/stores/storage_locations";

export const range = (to) => {
  let arr: number[] = []
  for(let i=0 ; i<to ; ++i){
    arr.push(i)
  }
  return arr
}

export const round_to_100 = (num: number) => Math.round(num/100) * 100

export const average = (values: number[]) => {
  if (!values.length) return undefined
  const total = values.reduce((sum, value) => sum + value, 0)
  return total / values.length
}

/**
 * Returns a shallow copy without null, undefined, or empty string values.
 * Useful for request params and payloads where omitted values should not be sent.
 */
export const clean_params = <T extends object>(params: T = {} as T): Partial<T> => {
  const cleaned: Partial<T> = {}
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      cleaned[key as keyof T] = value as T[keyof T]
    }
  })
  return cleaned
}


export type Equals<X, Y> =
    (<T>() => T extends X ? 1 : 2) extends
    (<T>() => T extends Y ? 1 : 2) ? true : false;

export function debounce(fn, wait_ms=300){
  let timer: any = 0;
  return function(...args){
    if(timer) {
      clearTimeout(timer); // clear any pre-existing timer
    }
    timer = setTimeout(()=>{
      fn.apply(args); // call the function if time expires
    }, wait_ms);
  }
}

export const random_string = (length: number=5) : string => (Math.random() + 1).toString(36).substring(length)

export const force_download_file = (data_blob: Blob, filename: string, mime_type: string) => {
  // Construct a new link and click it
  const a = document.createElement("a");
  const blob_url = URL.createObjectURL(data_blob)

  a.href = blob_url
  a.style.display = "none"
  a.download = filename
  // Add it to the DOM (Chrome is okay without it, but Firefox is not)
  document.body.appendChild(a)
  a.click()
  setTimeout(_ => {
    document.body.removeChild(a)
    URL.revokeObjectURL(blob_url)
  }, 1000)
}

export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ""))
    reader.onerror = () => reject(reader.error || new Error("Error reading file."))
    reader.readAsDataURL(file)
  })
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error("Error loading image."))
    image.src = src
  })
}

/**
 * Scales an image down so its long side fits maxLongSidePx.
 * With maxAspectRatio, images wider or taller than that ratio are padded with
 * transparent space (image centered) so they still read at small sizes.
 */
export async function imageFileToResizedDataUrl(file: File, maxLongSidePx: number, maxAspectRatio?: number): Promise<string> {
  if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
    throw new Error("Unsupported image type.")
  }

  const sourceDataUrl = await fileToDataUrl(file)
  const image = await loadImage(sourceDataUrl)
  const sourceWidth = image.naturalWidth || image.width
  const sourceHeight = image.naturalHeight || image.height

  if (!sourceWidth || !sourceHeight || maxLongSidePx <= 0) {
    return sourceDataUrl
  }

  const scale = Math.min(1, maxLongSidePx / Math.max(sourceWidth, sourceHeight))
  const targetWidth = Math.max(1, Math.round(sourceWidth * scale))
  const targetHeight = Math.max(1, Math.round(sourceHeight * scale))

  // Padding only grows the short side, so the long side stays within maxLongSidePx
  let canvasWidth = targetWidth
  let canvasHeight = targetHeight
  if (maxAspectRatio && maxAspectRatio >= 1) {
    canvasHeight = Math.max(targetHeight, Math.ceil(targetWidth / maxAspectRatio))
    canvasWidth = Math.max(targetWidth, Math.ceil(targetHeight / maxAspectRatio))
  }
  const padded = canvasWidth !== targetWidth || canvasHeight !== targetHeight

  const canvas = document.createElement("canvas")
  canvas.width = canvasWidth
  canvas.height = canvasHeight

  const context = canvas.getContext("2d")
  if (!context) {
    return sourceDataUrl
  }

  context.drawImage(
    image,
    Math.floor((canvasWidth - targetWidth) / 2),
    Math.floor((canvasHeight - targetHeight) / 2),
    targetWidth,
    targetHeight,
  )

  // JPEG has no transparency, so padded JPEGs are stored as PNG
  if (file.type === "image/webp" || (file.type === "image/jpeg" && !padded)) {
    return canvas.toDataURL(file.type, 0.9)
  }

  return canvas.toDataURL("image/png")
}

export const calculate_download_cost_bytes = (
  metric: FileDownloadMetrics,
  locations: StorageLocation[] = []
): { cost: number; bytes: number } => {
  const egress_cost_by_backend = new Map<number, number>();
  locations.forEach((location) => {
    if (location.egress_cost == null) return;
    egress_cost_by_backend.set(location.id, location.egress_cost);
  });

  const generations = Array.isArray(metric.generation_downloads)
    ? metric.generation_downloads
    : [];
  if (generations.length === 0) {
    console.error("calculate_download_cost_bytes: No generation downloads in FileDownloadMetrics: ", metric)
    throw new Error("calculate_download_cost_bytes: metric without generation_downloads");
  }

  let totalCost = 0;
  let totalBytes = 0;

  for (const generation of generations) {
    const servedSize = generation.served_size ?? generation.total_size;
    if (!servedSize){
      console.warn("calculate_download_cost_bytes: it seems that a generation download was zero bytes...?", generation)
      continue // skip to the next generation
    }

    const successfulFragments = get_downloaded_fragments_by_generation(generation);
    if (!successfulFragments.length){
      console.warn("calculate_download_cost_bytes: all fragment downloads failed...?", generation)
      continue // skip to the next generation
    }

    const perFragmentBytes = servedSize / successfulFragments.length;
    for (const fragment of successfulFragments) {
      const egressCost = egress_cost_by_backend.get(fragment.backend_id);
      if (egressCost == null){
        const msg = "calculate_download_cost_bytes: no egress cost for backend #"+fragment.backend_id
        console.error(msg, egress_cost_by_backend)
        return {cost: 0, bytes: 0}
      }
      const fragmentTb = perFragmentBytes / 1_000_000_000_000;
      totalCost += fragmentTb * egressCost;
      totalBytes += perFragmentBytes;
    }
  }
  if(totalBytes != metric.size){
    console.error("calculate_download_cost_bytes: calculated bytes does not match bytes from metric", totalBytes, metric.size)
  }
  return { cost: totalCost, bytes: totalBytes };
};

export const get_downloaded_fragments_by_generation = (
  generation: {
    fragments?: Array<{
      backend_id: number
      transfer_time_ms?: number
      estimated_time_ms?: number
      failed?: boolean
    }>
  }
) => {
  const fragments = Array.isArray(generation.fragments) ? generation.fragments : [];
  // Only count the successfully downloaded fragments, where transfer time was recorded (e.g., actually downloaded)
  return fragments.filter((fragment) => fragment.failed !== true && fragment.transfer_time_ms);
}

const to_radians = (value: number) => (value * Math.PI) / 180
const distance_km_between = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
  const earth_radius_km = 6371
  const dlat = to_radians(b.lat - a.lat)
  const dlng = to_radians(b.lng - a.lng)
  const lat1 = to_radians(a.lat)
  const lat2 = to_radians(b.lat)
  const h =
    Math.sin(dlat / 2) * Math.sin(dlat / 2) +
    Math.sin(dlng / 2) * Math.sin(dlng / 2) * Math.cos(lat1) * Math.cos(lat2)
  return 2 * earth_radius_km * Math.asin(Math.sqrt(h))
}

export const calculate_average_download_distance_km = (
  metrics: FileDownloadMetrics[],
  events: DownloadEvent[],
  locations: StorageLocation[],
  fly_region_locations: Record<string, FlyRegionLocation>
): number | undefined => {
  if (!metrics.length) return undefined

  const event_fly_region_by_id = new Map(
    (events || []).map(evt => [evt.id, evt.fly_region])
  )

  let total_weighted_distance_km = 0
  let total_bytes = 0

  for (const metric of metrics) {
    const fly_region = metric.fly_region || event_fly_region_by_id.get(metric.usage_event_id ?? -1)
    if (!fly_region) continue

    const fly_location = fly_region_locations[fly_region]
    if (!fly_location) continue

    const generations = Array.isArray(metric.generation_downloads)
      ? metric.generation_downloads
      : []
    if (!generations.length) continue

    for (const generation of generations) {
      const served_size = generation.served_size ?? generation.total_size
      if (!served_size) continue

      const successful_fragments = get_downloaded_fragments_by_generation(generation)
      if (!successful_fragments.length) continue

      const per_fragment_bytes = served_size / successful_fragments.length
      for (const fragment of successful_fragments) {
        const backend = locations.find(loc => loc.id == fragment.backend_id)
        if (!backend) continue
        const distance_km = distance_km_between(fly_location, backend)
        total_weighted_distance_km += distance_km * per_fragment_bytes
        total_bytes += per_fragment_bytes
      }
    }
  }

  if (!total_bytes) return undefined
  return total_weighted_distance_km / total_bytes
}

// Parse a date-like value as UTC, appending a Z offset when a timezone is missing.
export const parseUtcDate = (value: string | number | Date | null | undefined): Date => {
  if (value == null) throw "No value passed to parseUtcDate";
  if (value instanceof Date) return value;
  if (typeof value === "number") {
    const date = new Date(value);
    return new Date(Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
      date.getUTCMilliseconds()
    ));
  }
  return new Date(value.includes("Z") || /[+-]\d{2}:\d{2}$/.test(value) ? value : `${value}Z`);
};

export const parse_unix_date = (value: unknown): Date => {
  if (value instanceof Date) return value

  if (typeof value === "number") {
    return new Date(value > 1_000_000_000_000 ? value : value * 1000)
  }

  return new Date(value as string)
}

export const countryCodeToFlag = (code: string) => {
  if (!code || code.length !== 2) return "";
  const cc = code.toUpperCase();
  const A = 65;
  const base = 0x1f1e6;
  return String.fromCodePoint(
    base + (cc.charCodeAt(0) - A),
    base + (cc.charCodeAt(1) - A)
  );
};
